import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import inventory from "@/api/inventory";

const TYPE_COLORS = {
  entrada: "#10b981",
  salida: "#ef4444",
  salida_orden: "#f97316",
  devolucion: "#f59e0b",
  ajuste: "#a855f7",
  transferencia: "#06b6d4",
  interno_pick: "#8b5cf6",
  interno_stage: "#6366f1",
  ajuste_precio: "#0ea5e9",
  ajuste_metadata: "#64748b",
  desconocido: "#71717a",
};

const DOW_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function heatmapCellClass(count, max) {
  if (!count) return "bg-muted/20";
  const intensity = max > 0 ? count / max : 0;
  if (intensity > 0.75) return "bg-primary text-primary-foreground";
  if (intensity > 0.50) return "bg-primary/75 text-white";
  if (intensity > 0.25) return "bg-primary/50 text-white";
  if (intensity > 0.10) return "bg-primary/30";
  return "bg-primary/15";
}

export default function InventoryChartsGrid({ filters, testId = "inventory-charts", apiOverride }) {
  const { t } = useTranslation();
  const api = apiOverride || inventory;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getChartsData(filters)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.chartsLoad", { defaultValue: "No se pudieron cargar las gráficas" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const monthly = data?.monthly_in_out || [];
  const daily = data?.daily_trend || [];
  const types = data?.type_distribution || [];
  const heatmap = data?.user_dow_heatmap || [];

  const heatmapMax = useMemo(() => {
    let m = 0;
    for (const u of heatmap) {
      for (let i = 0; i < 7; i++) m = Math.max(m, u[`dow_${i}`] || 0);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="grid gap-3 lg:grid-cols-2">
        {/* 1. Barras apiladas — Entradas vs Salidas por mes */}
        <Card data-testid="chart-monthly-bars">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("inventory.charts.monthlyInOut", { defaultValue: "Entradas vs Salidas por mes" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading
              ? <Skeleton className="h-[260px] w-full" />
              : monthly.length === 0
                ? <div className="h-[260px] flex items-center justify-center text-muted-foreground text-xs">{t("inventory.empty", { defaultValue: "Sin datos" })}</div>
                : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="entradas" stackId="a" fill={TYPE_COLORS.entrada} name="Entradas" />
                      <Bar dataKey="salidas" stackId="a" fill={TYPE_COLORS.salida} name="Salidas" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
          </CardContent>
        </Card>

        {/* 2. Línea — Tendencia diaria */}
        <Card data-testid="chart-daily-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("inventory.charts.dailyTrend", { defaultValue: "Tendencia diaria de transacciones" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading
              ? <Skeleton className="h-[260px] w-full" />
              : daily.length === 0
                ? <div className="h-[260px] flex items-center justify-center text-muted-foreground text-xs">{t("inventory.empty", { defaultValue: "Sin datos" })}</div>
                : (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" interval={Math.max(0, Math.floor(daily.length / 8))} />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                      />
                      <Line type="monotone" dataKey="tx_count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Tx" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
          </CardContent>
        </Card>

        {/* 3. Pie — Distribución de canonical_type */}
        <Card data-testid="chart-type-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("inventory.charts.typeDistribution", { defaultValue: "Distribución por tipo canónico" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading
              ? <Skeleton className="h-[260px] w-full" />
              : types.length === 0
                ? <div className="h-[260px] flex items-center justify-center text-muted-foreground text-xs">{t("inventory.empty", { defaultValue: "Sin datos" })}</div>
                : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={types}
                        dataKey="count"
                        nameKey="canonical_type"
                        cx="40%"
                        cy="50%"
                        outerRadius={80}
                        label={({ pct }) => pct > 3 ? `${pct}%` : ""}
                        labelLine={false}
                      >
                        {types.map((t, i) => (
                          <Cell key={i} fill={TYPE_COLORS[t.canonical_type] || "#999"} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                        formatter={(v, n) => [`${Number(v).toLocaleString("en-US")} (${types.find((t) => t.canonical_type === n)?.pct || 0}%)`, n]}
                      />
                      <Legend
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                        wrapperStyle={{ fontSize: 10, lineHeight: "16px" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
          </CardContent>
        </Card>

        {/* 4. Heatmap usuario × día de semana */}
        <Card data-testid="chart-user-heatmap">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("inventory.charts.userDowHeatmap", { defaultValue: "Top usuarios × día de semana" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading
              ? <Skeleton className="h-[260px] w-full" />
              : heatmap.length === 0
                ? <div className="h-[260px] flex items-center justify-center text-muted-foreground text-xs">{t("inventory.empty", { defaultValue: "Sin datos" })}</div>
                : (
                  <div className="overflow-auto max-h-[260px]" data-testid="heatmap-grid">
                    <table className="text-[10px] w-full">
                      <thead>
                        <tr>
                          <th className="text-left p-1 pr-2 font-normal text-muted-foreground sticky left-0 bg-card">Usuario</th>
                          {DOW_LABELS.map((d) => (
                            <th key={d} className="p-1 font-normal text-muted-foreground">{d}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {heatmap.map((u) => (
                          <tr key={u.canonical_id} data-testid={`heatmap-row-${u.canonical_id}`}>
                            <td className="p-1 pr-2 max-w-[140px] truncate sticky left-0 bg-card border-r border-border">
                              {u.canonical_name}
                            </td>
                            {Array.from({ length: 7 }).map((_, i) => {
                              const v = u[`dow_${i}`] || 0;
                              return (
                                <td key={i} className="p-0">
                                  <div
                                    className={`m-0.5 rounded text-center px-1 py-1 tabular-nums ${heatmapCellClass(v, heatmapMax)}`}
                                    title={`${u.canonical_name} ${DOW_LABELS[i]}: ${v} tx`}
                                  >
                                    {v > 0 ? v.toLocaleString("en-US") : ""}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
